import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type {
  AnyIntelligenceSettings,
  IntelligenceFeatureSettings,
  IntelligenceSettingsResponse,
  DisqualifyReasonOption,
} from '@core/intelligence-types';
import {
  createSingleEntityStore,
  getSingleEntityActions,
  ngrxEvent,
  PickActions,
} from '@core/ngrx-legacy';

const {
  actions,
  reducer: IntelligenceSettingsReducer,
  selectors: IntelligenceSettingsSelectors,
  namespace: IntelligenceSettingsNamespace,
} = createSingleEntityStore<IntelligenceSettingsResponse>()('IntelligenceSettingsState', {
  customActions: adapter => {
    const defaultActions = getSingleEntityActions(adapter);
    return {
      toggleFeature: (
        state,
        payload: Partial<Record<AnyIntelligenceSettings['field'], boolean>> & { enabled?: boolean },
      ) => adapter.markAsPending(state),
      toggleFeatureSuccess: (state, payload: IntelligenceSettingsResponse) =>
        defaultActions.fetchSuccess(state, payload),
      toggleFeatureFailed: (state, payload: HttpErrorResponse) =>
        defaultActions.fetchFailed(state, payload),
      updateFeatureSettings: (state, payload: Partial<IntelligenceFeatureSettings>) =>
        adapter.markAsPending(state),
      updateFeatureSettingsSuccess: (state, payload: IntelligenceSettingsResponse) =>
        defaultActions.fetchSuccess(state, payload),
      updateFeatureSettingsFailed: (state, payload: HttpErrorResponse) =>
        defaultActions.fetchFailed(state, payload),
      fetchDisqualifyReasons: ngrxEvent<{ appGuid: string }>(),
      fetchDisqualifyReasonsSuccess: ngrxEvent<DisqualifyReasonOption[]>(),
      fetchDisqualifyReasonsFailed: ngrxEvent<HttpErrorResponse>(),
    };
  },
});

const selectIntelligenceFeatureSettings = createSelector(
  IntelligenceSettingsSelectors.data,
  data => data?.data ?? null,
);

const selectIntelligenceMasterEnabled = createSelector(
  IntelligenceSettingsSelectors.data,
  data => data?.enabled ?? true,
);

const IntelligenceSettingsActions: PickActions<
  typeof actions,
  'toggleFeature' | 'updateFeatureSettings' | 'fetchDisqualifyReasons' | 'fetch'
> = actions;

export {
  IntelligenceSettingsActions,
  IntelligenceSettingsReducer,
  IntelligenceSettingsSelectors,
  IntelligenceSettingsNamespace,
  selectIntelligenceFeatureSettings,
  selectIntelligenceMasterEnabled,
};
